package com.vizio.pay.api

import com.vizio.redwolf.dms.LinkedDevicesResponse
import com.vizio.redwolf.dms.homesetup.HomeSetupApi
import com.vizio.redwolf.dms.homesetup.HomeSetupEnvironment
import com.vizio.redwolf.dms.homesetup.LinkedDevicesFilter
import com.vizio.redwolf.dms.model.FieldedStatusRequestInfo
import com.vizio.redwolf.dms.model.LinkedDevicesResult
import com.vizio.redwolf.dms.model.NonceRegisterResponse
import com.vizio.redwolf.dms.model.NonceRegisterResult
import com.vizio.redwolf.dms.model.PayNonceRequestResponse
import com.vizio.redwolf.dms.model.PayNonceRequestResult
import com.vizio.redwolf.dms.model.PayNonceStatusResponse
import com.vizio.redwolf.dms.model.PayNonceStatusResult
import com.vizio.redwolf.dms.model.RequestNonceRequestInfo
import com.vizio.redwolf.dms.model.UnregisterAllResponse
import com.vizio.redwolf.dms.model.UnregisterAllResult
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import kotlin.js.Promise

@JsExport
class HomeSetupJsApi(homeSetupEnv: HomeSetupEnvironment, payEnvironment: PayEnvironment, jsClientConfig: JsClientConfig? = null) {
    private val setupApi = HomeSetupApi(
        homeSetupEnvironment = homeSetupEnv,
        payEnvironment = payEnvironment,
        appConfig = AppConfig(""),
        clientConfig = jsClientConfig.toClientConfig(WebApiUseCase.SDK_HEADERS)
    )

    @JsName("requestNonce")
    fun requestNonce(jsRequestNonceInfo: dynamic, retryOnRegistered: Boolean, sentryHeader: String?, traceId: String? = null): Promise<dynamic> {
        val data = JsExceptionInfo(caller = HomeSetupRequests.RequestNonce())
        return handleJsPromiseRequest(jsExceptionInfo = data, exceptionHandler = ::handleHomeSetupJsException) {
            val requestNonceInfo = RequestNonceRequestInfo(
                deviceInstanceId = jsRequestNonceInfo.deviceInstanceId as String,
                friendlyName = jsRequestNonceInfo.friendlyName as String,
                modelName = jsRequestNonceInfo.modelName as String,
                serialNumber = jsRequestNonceInfo.serialNumber as String,
                skipCreditCard = jsRequestNonceInfo.skipCreditCard as Boolean?
            )
            setupApi.requestNonce(requestNonceInfo, retryOnRegistered, sentryHeader, traceId = traceId)
        }
    }

    @JsName("requestNonceStatusFielded")
    fun requestNonceStatusFielded(jsRequestNonceInfo: dynamic, sentryHeader: String?): Promise<dynamic> {
        val data = JsExceptionInfo(caller = HomeSetupRequests.RequestNonceStatus())
        return handleJsPromiseRequest(jsExceptionInfo = data, exceptionHandler = ::handleHomeSetupJsException) {
            val requestInfo = FieldedStatusRequestInfo(
                pin = jsRequestNonceInfo.pin as String,
                isLookupUserId = jsRequestNonceInfo.isLookupUserId as Boolean?
            )
            setupApi.requestNonceStatusFielded(requestInfo, sentryHeader)
        }
    }

    @JsName("linkNonce")
    fun linkNonce(token: String, pin: String, sentryHeader: String? = null, traceId: String? = null): Promise<dynamic> {
        val data = JsExceptionInfo(caller = HomeSetupRequests.LinkNonce())
        return handleJsPromiseRequest(jsExceptionInfo = data, exceptionHandler = ::handleHomeSetupJsException) {
            setupApi.linkNonce(token, pin, sentryHeader, traceId = traceId)
        }
    }

    @JsName("unlink")
    fun unlink(deviceId: String, sentryHeader: String? = null, traceId: String? = null): Promise<dynamic> {
        val data = JsExceptionInfo(caller = HomeSetupRequests.Unlink())
        return handleJsPromiseRequest(jsExceptionInfo = data, exceptionHandler = ::handleHomeSetupJsException) {
            setupApi.unlink(deviceId, sentryHeader, traceId = traceId)
        }
    }

    @JsName("getLinkedDevices")
    fun getLinkedDevices(token: String, jsFilter: dynamic, sentryHeader: String? = null, traceId: String? = null): Promise<dynamic> {
        val data = JsExceptionInfo(caller = HomeSetupRequests.GetLinkedDevices())
        return handleJsPromiseRequest(jsExceptionInfo = data, exceptionHandler = ::handleHomeSetupJsException) {
            val filter = jsFilter as LinkedDevicesFilter
            setupApi.getLinkedDevices(token, filter, sentryHeader, traceId = traceId)
        }
    }

    private fun <T : Any> handleHomeSetupJsException(ex: Exception, data: JsExceptionInfo<T>? = null): dynamic {
        val exception = Exception(jsRequestExceptionError)
        return when (data?.caller) {
            is HomeSetupRequests.RequestNonce -> PayNonceRequestResult(ResponseHeaders.Invalid, PayNonceRequestResponse.Error(exception))
            is HomeSetupRequests.RequestNonceStatus -> PayNonceStatusResult(ResponseHeaders.Invalid, PayNonceStatusResponse.Error(exception))
            is HomeSetupRequests.LinkNonce -> NonceRegisterResult(ResponseHeaders.Invalid, NonceRegisterResponse.Error(exception))
            is HomeSetupRequests.Unlink -> UnregisterAllResult(ResponseHeaders.Invalid, UnregisterAllResponse.Error(exception))
            is HomeSetupRequests.GetLinkedDevices -> LinkedDevicesResult(ResponseHeaders.Invalid, LinkedDevicesResponse.Error(exception))
            else -> ex
        }
    }
}

private sealed class HomeSetupRequests {
    class RequestNonce : HomeSetupRequests()
    class RequestNonceStatus : HomeSetupRequests()
    class LinkNonce : HomeSetupRequests()
    class Unlink : HomeSetupRequests()
    class GetLinkedDevices : HomeSetupRequests()
}
